import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GlobalHelpButton from '@/components/common/GlobalHelpButton';
import { EmailVerificationBanner } from '@/components/common/EmailVerificationBanner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Bot, 
  Building2, 
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  ChevronLeft,
  Shield,
  MessageCircle,
  HelpCircle,
  Send,
  Paperclip,
  X,
  Loader2,
  CheckCircle,
  FileText,
  Bell,
  UserPlus,
  ExternalLink,
  PlayCircle,
  Users,
  Trophy,
  Gift,
  Target,
  Mail,
  Zap,
  BarChart3
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '@/services/api';
import ProductTour from '../ProductTour';
import GhostBanner from '../admin/GhostBanner';

const NavItem = ({ to, icon: Icon, label, active, collapsed }) => (
  <Link
    to={to}
    className={cn(
      "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
      active 
        ? "bg-blue-50 text-blue-600 border border-blue-100"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
      collapsed && "justify-center px-3"
    )}
    title={collapsed ? label : undefined}
  >
    <Icon className="w-5 h-5 flex-shrink-0" />
    {!collapsed && <span>{label}</span>}
  </Link>
);

// Pending Moderator Invitations Badge
// Surfaces moderator invites the current OWNER has sent that the invitees
// haven't accepted yet — emails get lost in spam ~40% of the time, so a
// one-click "Resend" nudge measurably improves activation.
const PendingInvitationsBadge = ({ isModeratorOnly }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ pending: [], stale_count: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    if (!user?.user_id) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      const { data } = await api.get('/me/pending-invitations');
      setData(data || { pending: [], stale_count: 0, total: 0 });
    } catch {
      // Silent fail — non-critical.
    }
  };

  useEffect(() => {
    if (isModeratorOnly) return undefined;
    if (!user?.user_id) return undefined;
    load();
    const id = setInterval(load, 300000); // 5-min poll
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [isModeratorOnly, user?.user_id]);

  // Moderators don't OWN any invitations — hide the badge for them.
  if (isModeratorOnly) return null;
  if (!data.total) return null;

  const resend = async (mod) => {
    setBusyId(mod.moderator_id);
    try {
      await api.post(`/chatbots/${mod.chatbot_id}/moderators/${mod.moderator_id}/resend-invite`);
      toast.success(`Reminder sent to ${mod.email}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not resend';
      toast.error(typeof msg === 'string' ? msg : 'Could not resend');
    } finally {
      setBusyId(null);
    }
  };

  const stale = data.stale_count > 0;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="pending-invitations-badge"
          title={`${data.total} pending moderator invitation${data.total === 1 ? '' : 's'}`}
        >
          <UserPlus className={`w-5 h-5 ${stale ? 'text-amber-600' : 'text-slate-600'}`} />
          <span className={`absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1.5 ${stale ? 'bg-amber-500' : 'bg-slate-500'} text-white text-[11px] rounded-full flex items-center justify-center font-medium`}>
            {data.total > 9 ? '9+' : data.total}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 bg-white border-slate-200 shadow-xl p-0">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900 text-sm">Pending moderator invitations</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {stale
              ? `${data.stale_count} invitation${data.stale_count === 1 ? ' is' : 's are'} stale (no reply for 7+ days)`
              : 'Recently invited — give them a couple of days before nudging.'}
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="pending-invitations-list">
          {data.pending.map(p => {
            const isStale = (p.days_pending || 0) >= 7;
            return (
              <div key={p.moderator_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{p.email}</p>
                    <p className="text-xs text-slate-500 truncate">
                      for <span className="font-medium text-slate-700">{p.chatbot_name}</span>
                    </p>
                    <p className={`text-[11px] mt-0.5 ${isStale ? 'text-amber-700' : 'text-slate-400'}`}>
                      Invited {p.days_pending === 0 ? 'today' : `${p.days_pending} day${p.days_pending === 1 ? '' : 's'} ago`}
                      {p.last_reminder_at && ' · reminder sent'}
                    </p>
                  </div>
                  <button
                    onClick={() => resend(p)}
                    disabled={busyId === p.moderator_id}
                    className={`text-xs px-2.5 py-1.5 rounded-md font-medium shrink-0
                      ${isStale
                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'}
                      disabled:opacity-60 disabled:cursor-not-allowed`}
                    data-testid={`resend-invite-${p.moderator_id}`}
                  >
                    {busyId === p.moderator_id ? 'Sending…' : 'Resend'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Notification Bell Dropdown
const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const loadNotifications = async () => {
    // Skip when there's no authenticated user OR the tab is hidden — both
    // cases produce a flood of redundant /notifications hits on production
    // (search-engine crawlers + background tabs).
    if (!user?.user_id) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    setLoading(true);
    try {
      const response = await api.get('/notifications?limit=10');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Silent — a 401 here just means the session expired; the auth flow
      // handles the redirect and we don't want to spam toast errors.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.user_id) return undefined;
    loadNotifications();
    // Slower poll (3 min) — the previous 60s cadence was hammering the
    // backend, and notifications aren't time-critical for users.
    const interval = setInterval(loadNotifications, 180000);
    // Refresh on tab refocus so users coming back from another tab see
    // the latest count immediately (without waiting for the next tick).
    const onFocus = () => loadNotifications();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.user_id]);

  const handleNotificationClick = async (notif) => {
    // Mark as read
    try {
      await api.patch(`/notifications/${notif.notification_id}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notif.notification_id ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    // Navigate to action URL if present
    if (notif.action_url) {
      navigate(notif.action_url);
    }
    setIsOpen(false);
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'low_credits':
      case 'credit_depleted':
        return '⚠️';
      case 'subscription_expiring':
        return '📅';
      case 'new_feature':
        return '✨';
      case 'system_update':
        return '🔄';
      case 'support_response':
        return '💬';
      default:
        return '🔔';
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button 
          className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No notifications</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <button
                key={notif.notification_id}
                onClick={() => handleNotificationClick(notif)}
                className={cn(
                  "w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0",
                  !notif.is_read && "bg-blue-50/50"
                )}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg">{getNotificationIcon(notif.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        "text-sm truncate",
                        !notif.is_read ? "font-semibold text-slate-900" : "text-slate-700"
                      )}>
                        {notif.title}
                      </p>
                      {!notif.is_read && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notif.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {notif.action_url && (
                    <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>
        {notifications.length > 0 && (
          <div className="border-t border-slate-100 p-2">
            <button 
              onClick={() => { navigate('/dashboard/settings?tab=notifications'); setIsOpen(false); }}
              className="w-full py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              View all notifications
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Support & Feedback Modal
const SupportFeedbackModal = ({ isOpen, onClose }) => {
  const [type, setType] = useState('support');
  const [issueType, setIssueType] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const issueTypes = [
    'Bug Report',
    'Feature Not Working',
    'Account Issue',
    'Billing Question',
    'Integration Help',
    'Performance Issue',
    'Other'
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024); // 5MB max
    if (validFiles.length !== files.length) {
      toast.error('Some files exceed 5MB limit and were not added');
    }
    setAttachments(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('subject', subject || (type === 'support' ? `${issueType} Issue` : 'Feature Feedback'));
      formData.append('description', description);
      if (type === 'support') {
        formData.append('issue_type', issueType);
      }
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      await api.post('/support/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        resetForm();
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setType('support');
    setIssueType('');
    setSubject('');
    setDescription('');
    setAttachments([]);
  };

  if (success) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md bg-white border-slate-200">
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank you!</h3>
            <p className="text-slate-600">Your {type === 'support' ? 'request' : 'feedback'} has been submitted.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white border-slate-200 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Support & Feedback</DialogTitle>
          <DialogDescription className="text-slate-600">
            Let us know how we can help or share your ideas
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Type Selection */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setType('support')}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
                type === 'support'
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              )}
            >
              <HelpCircle className="w-5 h-5" />
              Support
            </button>
            <button
              type="button"
              onClick={() => setType('feedback')}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
                type === 'feedback'
                  ? "border-purple-500 bg-purple-50 text-purple-700"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              )}
            >
              <MessageCircle className="w-5 h-5" />
              Feedback
            </button>
          </div>

          {/* Support-specific fields */}
          {type === 'support' && (
            <div className="space-y-2">
              <Label className="text-slate-700">Issue Type</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {issueTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Subject */}
          <div className="space-y-2">
            <Label className="text-slate-700">Subject (Optional)</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={type === 'support' ? 'Brief description of the issue' : 'Your idea in a few words'}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-slate-700">
              {type === 'support' ? 'Describe your issue' : 'Share your feedback'}
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={type === 'support' 
                ? 'Please provide as much detail as possible...' 
                : 'What feature would you like to see? How can we improve?'
              }
              rows={5}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 resize-none"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label className="text-slate-700">Attachments (Optional)</Label>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer transition-colors">
                <Paperclip className="w-4 h-4" />
                <span className="text-sm">Add files</span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
              </label>
              <span className="text-xs text-slate-500">Max 5 files, 5MB each</span>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachments.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm text-slate-700">
                    <span className="truncate max-w-[120px]">{file.name}</span>
                    <button type="button" onClick={() => removeAttachment(index)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <Button 
            type="submit" 
            className={cn(
              "w-full text-white",
              type === 'support' ? "bg-blue-600 hover:bg-blue-700" : "bg-purple-600 hover:bg-purple-700"
            )}
            disabled={submitting || (type === 'support' && !issueType)}
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Submit {type === 'support' ? 'Request' : 'Feedback'}</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DashboardLayout = ({ children }) => {
  const { user, logout, hasFeature } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  // Initialize from localStorage synchronously to avoid flash
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('harmonix-sidebar-collapsed');
      return saved === 'true';
    }
    return false;
  });
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/billing/subscription');
        setSubscription(response.data);
      } catch (error) {
        // Default to free tier if no subscription
        setSubscription({ tier: 'free' });
      }
    };
    fetchSubscription();
  }, []);

  // Harmonix Assistant widget removed - will be added via embed code later

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('harmonix-sidebar-collapsed', String(newState));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [];
  
  // Core navigation - Most used items first
  navItems.push({ to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true, emoji: '🏠' });
  navItems.push({ to: '/dashboard/chatbots', icon: Bot, label: 'AI Employees', emoji: '🤖' });
  
  // Feature-gated navigation items - only show if user has access
  if (hasFeature('leads_crm')) {
    navItems.push({ to: '/dashboard/leads', icon: Target, label: 'Leads CRM', emoji: '🎯' });
  }
  if (hasFeature('analytics')) {
    navItems.push({ to: '/dashboard/analytics', icon: BarChart3, label: 'Analytics', emoji: '📊' });
  }
  // Automations are now managed per-agent (Feb 2026 per user request).
  // Access them via Agent → Automations tab inside each AI Employee.
  // Email Channel is now part of agent creation → Channels section.
  // Sidebar entry removed (Feb 2026 per user request).
  
  // Add admin link for system admins
  const isSystemAdmin = user?.role === 'system_admin';
  const isReseller = user?.role === 'reseller';
  // Moderator-only accounts see a much-reduced sidebar.
  const isModeratorOnly = !!user?.is_moderator_only;

  // Moderator Performance — visible to any account owner (moderators are a
  // core feature; the page is empty for accounts that haven't added any).
  if (!isModeratorOnly) {
    navItems.push({ to: '/dashboard/moderator-performance', icon: Users, label: 'Moderators', emoji: '🧑‍✈️' });
  }

  // Unified "Refer & Earn" — combines Affiliate, Reseller Portal, and Leaderboard
  // into a single entry with internal tabs (cleaner Emergent-style UX).
  const showReferEarn =
    !isModeratorOnly && (
      isReseller ||
      isSystemAdmin ||
      hasFeature('affiliate_program') ||
      hasFeature('reseller_portal')
    );
  if (showReferEarn) {
    navItems.push({
      to: '/dashboard/refer',
      icon: Gift,
      label: 'Refer & Earn',
      section: 'growth',
      emoji: '🎁',
    });
  }
  
  // Settings & Admin - Always last (hidden for moderator-only accounts)
  if (!isModeratorOnly) {
    navItems.push({ to: '/dashboard/settings', icon: Settings, label: 'Settings', section: 'settings', emoji: '⚙️' });
  }
  
  if (isSystemAdmin) {
    navItems.push({ to: '/dashboard/audit-logs', icon: FileText, label: 'Audit Logs', adminOnly: true, section: 'admin', emoji: '📋' });
    navItems.push({ to: '/dashboard/admin', icon: Shield, label: 'Admin Panel', adminOnly: true, section: 'admin', emoji: '🛡️' });
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <GhostBanner />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 bottom-0 z-50 transform transition-all duration-300 lg:translate-x-0 bg-white border-r border-slate-100 shadow-sm",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
        sidebarCollapsed ? "w-20" : "w-72"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo & Collapse Toggle */}
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white">
            <Link to="/dashboard" className={cn("flex items-center gap-3", sidebarCollapsed && "justify-center w-full")}>
              {sidebarCollapsed ? (
                <img
                  src="/favicon.png"
                  alt="Harmonix"
                  className="h-9 w-9 object-contain transition-all"
                  data-testid="sidebar-logo-collapsed"
                />
              ) : (
                <img
                  src="/harmonix-logo.png"
                  alt="Harmonix"
                  className="h-9 w-auto object-contain transition-all"
                  data-testid="sidebar-logo-full"
                />
              )}
            </Link>
            <button
              onClick={toggleSidebar}
              className={cn(
                "p-2 rounded-lg hover:bg-slate-100 text-slate-500 hidden lg:flex",
                sidebarCollapsed && "absolute -right-10 top-4 bg-white border border-slate-200 shadow-sm"
              )}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="sidebar-collapse-toggle"
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <div key={item.to} className="relative">
                <NavItem 
                  {...item}
                  collapsed={sidebarCollapsed}
                  active={location.pathname === item.to || 
                          (item.to !== '/dashboard' && location.pathname.startsWith(item.to))}
                />
                {item.adminOnly && !sidebarCollapsed && (
                  <Badge className="absolute -top-1 -right-1 bg-indigo-500 text-[10px] px-1.5 py-0.5">
                    Admin
                  </Badge>
                )}
              </div>
            ))}
          </nav>

          {/* Support & Feedback Button */}
          <div className={cn("px-3 py-2 space-y-2", sidebarCollapsed && "px-2")}>
            {/* User Manual Link */}
            <a
              href="/docs/user-manual"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-slate-600 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-200",
                sidebarCollapsed && "justify-center px-3"
              )}
              title={sidebarCollapsed ? "User Manual" : undefined}
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>User Manual</span>}
            </a>
            {/* Support & Feedback Button */}
            <button
              onClick={() => setShowSupportModal(true)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-slate-600 hover:text-purple-600 hover:bg-purple-50 border border-slate-200 hover:border-purple-200",
                sidebarCollapsed && "justify-center px-3"
              )}
              title={sidebarCollapsed ? "Support & Feedback" : undefined}
            >
              <MessageCircle className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>Support & Feedback</span>}
            </button>
          </div>

          {/* Upgrade Banner for Free Users */}
          {subscription?.tier === 'free' && !sidebarCollapsed && (
            <div className="px-3 py-2">
              <Link
                to="/pricing"
                className="block p-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm font-semibold">Upgrade Your Plan</span>
                  </div>
                  <p className="text-xs text-white/80">Unlock more AI agents, messages & features</p>
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5" />
                </div>
              </Link>
            </div>
          )}
          {subscription?.tier === 'free' && sidebarCollapsed && (
            <div className="px-2 py-2">
              <Link
                to="/pricing"
                className="flex items-center justify-center w-full p-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white relative overflow-hidden"
                title="Upgrade Plan"
              >
                <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                <Zap className="w-5 h-5 relative z-10" />
              </Link>
            </div>
          )}

          {/* User Menu */}
          <div className={cn("p-3 border-t border-slate-200", sidebarCollapsed && "p-2")}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-slate-100",
                  sidebarCollapsed && "justify-center p-2"
                )}>
                  <Avatar className={cn("w-10 h-10", sidebarCollapsed && "w-8 h-8")}>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  {!sidebarCollapsed && (
                    <>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium truncate text-slate-900">{user?.name}</p>
                        <p className="text-xs truncate text-slate-500">{user?.email}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-500 focus:text-red-600 focus:bg-red-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:pl-20" : "lg:pl-72"
      )}>
        {/* Desktop Header with Notification Bell */}
        <header className="hidden lg:flex sticky top-0 z-30 backdrop-blur-xl border-b bg-white/80 border-slate-200 items-center justify-end gap-1 px-8 py-3">
          <PendingInvitationsBadge isModeratorOnly={isModeratorOnly} />
          <NotificationBell />
        </header>

        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl border-b bg-white/80 border-slate-200">
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-6 h-6 text-slate-900" />
            </button>
            <Link
              to="/dashboard"
              className="inline-flex"
              data-testid="mobile-header-logo-link"
              onClick={() => setSidebarOpen(false)}
            >
              <img
                src="/harmonix-logo.png"
                alt="Harmonix"
                className="h-8 w-auto object-contain"
                data-testid="mobile-header-logo"
              />
            </Link>
            <div className="flex items-center gap-1">
              <PendingInvitationsBadge isModeratorOnly={isModeratorOnly} />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <EmailVerificationBanner />
          {children}
        </main>
        <GlobalHelpButton />
      </div>

      {/* Support & Feedback Modal */}
      <SupportFeedbackModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
      
      {/* Product Tour for New Users */}
      <ProductTour 
        onComplete={() => toast.success('Tour completed! You\'re ready to go.')}
        onSkip={() => toast.info('Tour skipped. You can restart it from Settings.')}
      />
    </div>
  );
};

export default DashboardLayout;
